import { PencilIcon, PlusIcon } from '@phosphor-icons/react';
import { useField } from 'formik';
import { useCallback, useMemo } from 'react';
import { useSWRConfig } from 'swr';

import { ISimpleComboboxItem } from '../../../components/combobox/SimpleCombobox';
import { SimpleComboboxField } from '../../../components/combobox/SimpleComboboxField';
import { useCargoTypes, CARGO_TYPES_SWR_KEY } from '../models';
import { useTranslation } from '../../../contexts/translation-context';
import { getLocalisedName } from '@utils/localisation';
import { CreateCargoTypeDialog } from './CreateCargoTypeDialog';
import { UpdateCargoTypeDialog } from './UpdateCargoTypeDialog';

export interface IMinimalCargoType {
  id: string;
  nameNl: string;
  nameFr?: string;
  nameEn?: string;
  nameDe?: string;
}

export const cargoTypeToComboboxItem = (val: IMinimalCargoType, lang: string): ISimpleComboboxItem => {
  return {
    key: val.id,
    name: `${val.id} - ${getLocalisedName(val, lang)}`,
  };
};

export interface ICargoTypeComboboxProps {
  name: string;
  labelText?: string;
  isDisabled?: boolean;
  isInvalid?: boolean;
  onBlur?: () => void;
  isNullable?: boolean;
  onlySelect?: boolean;
}

export const CargoTypeComboboxField: React.FC<ICargoTypeComboboxProps> = (props) => {
  const { labelText, name, onlySelect, ...otherProps } = props;
  const { data } = useCargoTypes();
  const { mutate } = useSWRConfig();
  const [field, _meta, helpers] = useField({ name });
  const { lang } = useTranslation();

  const cargoTypes = data ?? [];

  const items: ISimpleComboboxItem[] = useMemo(() => {
    return data ? data.map((cargoType) => cargoTypeToComboboxItem(cargoType, lang)) : [];
  }, [cargoTypes, lang]);

  const refetch = useCallback(() => {
    mutate(CARGO_TYPES_SWR_KEY);
  }, [mutate]);

  const value: ISimpleComboboxItem | null = field.value;
  return (
    <div>
      <div className="flex items-center">
        <SimpleComboboxField labelText={labelText ?? 'Aard'} items={items} name={name} {...otherProps} />

        {!onlySelect && (
          <div className="flex-end flex justify-end gap-2 ml-2">
            {value && (
              <UpdateCargoTypeDialog
                id={value.key}
                onSubmit={(data) => {
                  helpers.setValue(cargoTypeToComboboxItem(data, lang));
                  refetch();
                }}
                triggerText={<PencilIcon className="button-icon" />}
              />
            )}

            <CreateCargoTypeDialog
              onSubmit={(data) => {
                helpers.setValue(cargoTypeToComboboxItem(data, lang));
                refetch();
              }}
              triggerText={<PlusIcon className="button-icon" />}
            />
          </div>
        )}
      </div>
    </div>
  );
};
